import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, Card, Form, Button, Row, Col, Alert, Spinner } from "react-bootstrap";
import { FaBook, FaCheckCircle, FaExclamationCircle, FaUser, FaInfoCircle, FaPhone } from "react-icons/fa";
import { getFirestore, addDoc, collection, serverTimestamp, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import sendCustomEmail from "../../functions/sendCustomEmail";

const db = getFirestore(firebaseApp);

function PublicReclamaciones() {
  const [searchParams] = useSearchParams();
  const rucParam = searchParams.get("ruc");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [empresa, setEmpresa] = useState(null);

  const [formData, setFormData] = useState({
    cliente_nombre: "",
    cliente_dni: "",
    cliente_email: "",
    cliente_telefono: "",
    tipo: "Reclamación", 
    detalle: "",
    pedido: ""
  });

  useEffect(() => {
    if (rucParam) {
      buscarEmpresa();
    }
  }, [rucParam]);

  const buscarEmpresa = async () => {
    try {
      const q = query(collection(db, "company"), where("ruc", "==", rucParam));
      const snap = await getDocs(q);
      if (!snap.empty) {
        setEmpresa(snap.docs[0].data());
      }
    } catch (e) {
      console.error("Error buscando empresa:", e);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rucParam) {
      setError("Error: RUC de empresa no especificado.");
      return;
    }

    try {
      setLoading(true);
      
      const qNum = query(
        collection(db, "reclamaciones"),
        where("ruc_empresa", "==", rucParam),
        orderBy("num_hoja", "desc"),
        limit(1)
      );
      const numSnap = await getDocs(qNum);
      let nextNum = 1;
      if (!numSnap.empty) {
        nextNum = (numSnap.docs[0].data().num_hoja || 0) + 1;
      }

      const docData = {
        ...formData,
        ruc_empresa: rucParam,
        num_hoja: nextNum,
        fecha: serverTimestamp(),
        estado: "Pendiente",
        created_at: serverTimestamp()
      };

      await addDoc(collection(db, "reclamaciones"), docData);

      // --- Notificación por Correo ---
      try {
        const qAdmins = query(
          collection(db, "users"), 
          where("rucEmpresa", "==", rucParam), 
          where("rol", "==", "admin")
        );
        const adminSnap = await getDocs(qAdmins);
        const adminEmails = [];
        adminSnap.forEach(doc => {
          if (doc.data().email) adminEmails.push(doc.data().email);
        });

        if (adminEmails.length > 0) {
          const subject = `Nuevo Reclamo - H-${String(nextNum).padStart(3, '0')} - ${empresa?.name || rucParam}`;
          const body = `Se ha registrado una nueva ${formData.tipo.toLowerCase()} en el Libro de Reclamaciones.\n\n` +
                       `Cliente: ${formData.cliente_nombre}\n` +
                       `Documento: ${formData.cliente_dni}\n` +
                       `Detalle: ${formData.detalle}\n\n` +
                       `Revise el panel para responder.`;
          
          await sendCustomEmail(adminEmails[0], adminEmails.slice(1).join(","), null, subject, body, "mail");
        }
      } catch (errEmail) {
        console.error("Error enviando notificación:", errEmail);
      }

      setSubmitted(true);
    } catch (err) {
      setError("Ocurrió un error al enviar el formulario.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-vh-100 d-flex align-items-center" style={{ background: "linear-gradient(135deg, #f3e8ff 0%, #ffffff 100%)" }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={6}>
              <Card className="text-center shadow-lg border-0 rounded-4 p-5">
                <FaCheckCircle className="text-success mx-auto mb-4" size={80} />
                <h2 className="fw-bold text-dark mb-3">Reclamo Enviado</h2>
                <p className="text-muted mb-4">Su requerimiento ha sido registrado en nuestro Libro de Reclamaciones.</p>
                <div className="bg-light p-3 rounded-pill mb-4 border">
                   <small className="text-muted d-block">Número de Seguimiento:</small>
                   <span className="h5 fw-bold text-primary" style={{ color: "#7c3aed" }}>R-{String(new Date().getFullYear())}-{rucParam}</span>
                </div>
                <Button variant="primary" className="px-5 rounded-pill py-2" onClick={() => window.location.reload()} style={{ background: "#7c3aed", borderColor: "#7c3aed" }}>
                  Volver al Formulario
                </Button>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100 pb-5" style={{ background: "linear-gradient(135deg, #f3f0ff 0%, #ffffff 100%)" }}>
      {/* Header Estilo Premium */}
      <div className="py-5 text-white shadow-sm mb-5" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <h1 className="fw-bold mb-1"><FaBook className="me-3" /> Libro de Reclamaciones</h1>
              <p className="opacity-75 mb-0">Atención oficial al consumidor por medios digitales</p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
               <div className="bg-white bg-opacity-20 rounded-4 p-3 d-inline-block text-start border border-white border-opacity-25">
                  <small className="d-block opacity-75">Empresa / Proveedor:</small>
                  <span className="fw-bold fs-5">{empresa?.name || rucParam}</span>
               </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
               <Card.Body className="p-4 p-lg-5">
                  {error && <Alert variant="danger" className="rounded-3"><FaExclamationCircle className="me-2" /> {error}</Alert>}
                  
                  <Form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
                            <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-3" style={{ background: "rgba(124, 58, 237, 0.1)", color: "#7c3aed" }}>
                                <FaUser size={20} />
                            </div>
                            <h5 className="fw-bold mb-0">1. Datos del Consumidor</h5>
                        </div>
                        <Row className="g-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-secondary">Nombre Completo</Form.Label>
                                    <Form.Control name="cliente_nombre" required className="form-control-lg bg-light border-0" value={formData.cliente_nombre} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-secondary">Documento (DNI/CE)</Form.Label>
                                    <Form.Control name="cliente_dni" required className="form-control-lg bg-light border-0" value={formData.cliente_dni} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-secondary">Teléfono</Form.Label>
                                    <Form.Control name="cliente_telefono" className="form-control-lg bg-light border-0" value={formData.cliente_telefono} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-secondary">Correo para Notificación</Form.Label>
                                    <Form.Control name="cliente_email" type="email" required className="form-control-lg bg-light border-0" value={formData.cliente_email} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    <div className="mb-5">
                        <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
                            <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-3" style={{ background: "rgba(124, 58, 237, 0.1)", color: "#7c3aed" }}>
                                <FaInfoCircle size={20} />
                            </div>
                            <h5 className="fw-bold mb-0">2. Detalle de Incidencia</h5>
                        </div>
                        
                        <Form.Group className="mb-4">
                            <Form.Label className="small fw-bold text-secondary">Tipo de Incidente</Form.Label>
                            <div className="d-flex gap-4 p-3 bg-light rounded-3">
                                <Form.Check type="radio" label="Reclamación" name="tipo" value="Reclamación" checked={formData.tipo === "Reclamación"} onChange={handleInputChange} id="t1" />
                                <Form.Check type="radio" label="Queja" name="tipo" value="Queja" checked={formData.tipo === "Queja"} onChange={handleInputChange} id="t2" />
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="small fw-bold text-secondary">Descripción Detallada</Form.Label>
                            <Form.Control as="textarea" rows={4} required name="detalle" className="form-control-lg bg-light border-0" style={{ resize: "none" }} value={formData.detalle} onChange={handleInputChange} />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="small fw-bold text-secondary">Pedido / Pretensión</Form.Label>
                            <Form.Control as="textarea" rows={2} required name="pedido" className="form-control-lg bg-light border-0" style={{ resize: "none" }} value={formData.pedido} onChange={handleInputChange} />
                        </Form.Group>
                    </div>

                    <Button variant="primary" type="submit" className="w-100 py-3 fw-bold rounded-pill shadow-lg border-0" disabled={loading} style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" }}>
                        {loading ? <Spinner animation="border" size="sm" /> : "ENVIAR MI RECLAMO"}
                    </Button>

                    <div className="mt-4 text-center">
                       <small className="text-muted"><FaInfoCircle className="me-1" /> Conforme a la Ley N° 29733 de Protección de Datos Personales.</small>
                    </div>
                  </Form>
               </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default PublicReclamaciones;
